import { get } from 'lodash';
import JWT_Decode from 'jwt-decode';

import {
  BE_BUILD_DATE,
  BE_BUILD_VERSION,
  LOCAL_USER,
  RES_AUTH_TOKEN,
  RES_AUTH_TOKEN_BACKUP,
  RES_AUTH_USER_BACKUP,
  RES_IS_DOUBLE_LOGIN
} from '../constants';
import { AuthUserModel, AuthUserRole } from '@/models/AuthUserModel';

export const parseRoles = (token: string): AuthUserRole[] => {
  try {
    const parsed = parseToken(token);
    if (parsed == null) {
      return [];
    }
    if ('roles' in parsed) {
      return parsed.roles as AuthUserRole[];
    }
    return [];
  } catch {
    return [];
  }
};

const getAndParseToken = (): any | null => {
  try {
    const token: string | null = AuthUserStore.getAuthToken();
    if (token == null) {
      return null;
    }
    return parseToken(token);
  } catch {
    return null;
  }
};

export const parseToken = (token?: string): any | null => {
  try {
    if (token == null) {
      return null;
    }
    return JSON.parse(JSON.stringify(JWT_Decode(token)));
  } catch {
    return null;
  }
};

export const setAuthLogin = login => {
  AuthUserStore.setAuthToken(login.token);
  AuthUserStore.setAuthUser(login.user || {});
};

export const getRolesForPayload = (payload?: any): AuthUserRole[] => {
  if (payload == null) {
    return [];
  }
  if ('roles' in payload) {
    return payload.roles as AuthUserRole[];
  }
  return [];
};

type LockStatus = 'unlocked' | 'locked' | 'refresh';

const AuthUserStore = {
  getStoreId: (): number | null => {
    const parsed: any | null = getAndParseToken();
    if (parsed == null) {
      return null;
    }
    if ('store' in parsed) {
      return parsed.store;
    }
    return null;
  },
  getStoreCode: (): string | null => {
    try {
      const user = localStorage.getItem(LOCAL_USER);
      if (user == null) {
        return null;
      }
      const userObj = JSON.parse(user);
      return get(userObj, 'store.storeCode');
    } catch {
      return null;
    }
  },
  getRoles: (): AuthUserRole[] => {
    const parsed: any | null = getAndParseToken();
    if (parsed == null) {
      return [];
    }
    if ('roles' in parsed) {
      return parsed.roles as AuthUserRole[];
    }
    return [];
  },
  setAuthUser: user => {
    localStorage.setItem('USER', JSON.stringify(user || {}));
  },
  getAuthUser: () => {
    try {
      const user: AuthUserModel = JSON.parse(localStorage.getItem('USER'));
      if (Object.keys(user).length === 0 && user.constructor === Object) {
        return null;
      }
      return user;
    } catch {
      // just don't crash
    }
  },
  setAuthToken: token => {
    localStorage.setItem(RES_AUTH_TOKEN, token);
  },
  getAuthToken: () => localStorage.getItem(RES_AUTH_TOKEN),
  setBuildDate: date => {
    localStorage.setItem(BE_BUILD_DATE, date);
  },
  getBuildDate: () => {
    try {
      const date = localStorage.getItem(BE_BUILD_DATE);
      return date;
    } catch {
      // just don't crash
    }
  },
  setBuildVersion: version => {
    localStorage.setItem(BE_BUILD_VERSION, version);
  },
  getBuildVersion: () => {
    try {
      const version = localStorage.getItem(BE_BUILD_VERSION);
      return version;
    } catch {
      // just don't crash
    }
  },
  getLockStatus: (): LockStatus => {
    const token = localStorage.getItem(RES_AUTH_TOKEN);
    const parsed = parseToken(token);
    if (parsed == null) {
      return 'unlocked';
    }
    if ('isLocked' in parsed) {
      return 'locked';
    }
    const refreshNeeded = localStorage.getItem('REFRESH_NEEDED');
    if (refreshNeeded) {
      return 'refresh';
    }
    return 'unlocked';
  },
  checkRefresh: () => {
    const lockStatus = AuthUserStore.getLockStatus();
    if (lockStatus === 'locked') {
      localStorage.setItem('REFRESH_NEEDED', 'true');
    } else {
      localStorage.removeItem('REFRESH_NEEDED');
    }

    return lockStatus;
  },

  // Backup start
  setPrvStorageBackup: () => {
    localStorage.setItem(RES_IS_DOUBLE_LOGIN, '1');
    localStorage.setItem(RES_AUTH_USER_BACKUP, localStorage.getItem('USER'));
    localStorage.setItem(
      RES_AUTH_TOKEN_BACKUP,
      localStorage.getItem('RES_AUTH_TOKEN')
    );
  },
  getPrvStorageBackup: () => {
    try {
      const doubleLogin = localStorage.getItem(RES_IS_DOUBLE_LOGIN);
      const user = localStorage.getItem(RES_AUTH_USER_BACKUP);
      const token = localStorage.getItem(RES_AUTH_TOKEN_BACKUP);
      return { doubleLogin, user, token };
    } catch {
      // just don't crash
    }
  },

  restorePrvStorageBackup: () => {
    localStorage.removeItem(RES_IS_DOUBLE_LOGIN);
    localStorage.removeItem(RES_AUTH_USER_BACKUP);
    localStorage.removeItem(RES_AUTH_TOKEN_BACKUP);
  },
  // Backup end

  removeAuthUser: () => {
    localStorage.removeItem('USER');
  },
  removeAuthToken: () => {
    localStorage.removeItem(RES_AUTH_TOKEN);
  },
  logout: () => {
    localStorage.removeItem(RES_AUTH_TOKEN);
    localStorage.removeItem('USER');
    localStorage.removeItem('recentlyViewed');
    localStorage.removeItem('REFRESH_NEEDED');
    // important that we get a page reload so in memory caches are cleared
    window.location.href = '/auth/login';
  }
};

export default AuthUserStore;
